import {
	SET_ACCOUNT_INFO,
	SET_MEMBER_LOOKUP,
	CLEAR_STATE,
	SET_REDEMPTION_SUMMARY,
	SET_REGISTER_CUSTOMER,
	SET_SUBMIT_REDEMPTION,
	SET_VOID_REDEMPTION,
	SET_UNLINK_ACCOUNT_INFO
} from './oem-rewards-mutation-types';
import api from '@/services/api-service';
import buildRoute from '@/composables/build-route';

export default {
	strict: process.env.NODE_ENV !== 'production',
	name: 'oemRewards',
	namespaced: true,
	state: {
		accountInfo: undefined,
		unlinkAccountInfo: undefined,
		accountStatuses: {
			Eligible: 'Eligible',
			Inactive: 'Inactive',
			Unlinked: 'Unlinked',
			NotFound: 'NotFound',
			DataMismatch: 'DataMismatch',
			DealerNotEligible: 'DealerNotEligible',
			RegistrationIncomplete: 'RegistrationIncomplete',
			InactiveUnlinked: 'InactiveUnlinked'
		},
		redemptionSummary: undefined,
		registerCustomer: undefined,
		submitRedemption: undefined,
		voidRedemption: undefined
	},
	mutations: {
		[SET_ACCOUNT_INFO](state, accountInfo) {
			state.accountInfo = accountInfo;
		},
		[SET_UNLINK_ACCOUNT_INFO](state, unlinkAccountInfo) {
			state.unlinkAccountInfo = unlinkAccountInfo;
		},
		[SET_MEMBER_LOOKUP](state, memberLookup) {
			state.accountInfo = { member: memberLookup };
		},
		[CLEAR_STATE](state) {
			state.accountInfo = undefined;
			state.memberLookup = undefined;
		},
		[SET_REDEMPTION_SUMMARY](state, redemptionSummary) {
			state.redemptionSummary = redemptionSummary;
		},
		[SET_REGISTER_CUSTOMER](state, registerCustomer) {
			state.registerCustomer = registerCustomer;
		},
		[SET_SUBMIT_REDEMPTION](state, submitRedemption) {
			state.submitRedemption = submitRedemption;
		},
		[SET_VOID_REDEMPTION](state, voidRedemption) {
			state.voidRedemption = voidRedemption;
		}
	},
	actions: {
		// actions let us get to ({ state, getters, commit, dispatch })
		clearStateAction({ commit }) {
			commit(CLEAR_STATE);
		},
		async getAccountInfoAction(
			{ commit },
			{ companyId, integrationId, customerId, email, errorHandler }
		) {
			const accountInfo = await api.get(
				buildRoute(
					'/oem/company/{companyId}/rewards/{integrationId}/account',
					{
						companyId: companyId,
						integrationId: integrationId,
						query: {
							customerId,
							email
						}
					}
				),
				errorHandler
			);
			commit(SET_ACCOUNT_INFO, accountInfo);
		},
		async memberLookupAction(
			{ commit },
			{ companyId, integrationId, searchParams }
		) {
			const memberLookup = await api.get(
				buildRoute(
					'/oem/company/{companyId}/rewards/{integrationId}/member',
					{
						companyId: companyId,
						integrationId: integrationId,
						query: searchParams
					}
				)
			);

			commit(SET_MEMBER_LOOKUP, memberLookup);
		},
		async getRedemptionSummaryAction(
			{ commit },
			{ companyId, integrationId, entityId, entityTypeId }
		) {
			const redemptionSummary = await api.get(
				buildRoute(
					'/oem/company/{companyId}/rewards/{integrationId}/redemption/{entityId}/{entityTypeId}',
					{
						companyId: companyId,
						integrationId: integrationId,
						entityId: entityId,
						entityTypeId: entityTypeId
					}
				)
			);

			commit(SET_REDEMPTION_SUMMARY, redemptionSummary);
			commit(SET_ACCOUNT_INFO, redemptionSummary.account);
		},
		async setAccountInfoAction({ commit }, accountInfo) {
			commit(SET_ACCOUNT_INFO, accountInfo);
		},
		async saveRegisterCustomerAction(
			{ commit },
			{ companyId, integrationId, parameters }
		) {
			const postRegisterCustomer = await api.post(
				buildRoute(
					'/oem/company/{companyId}/rewards/{integrationId}/member/register',
					{
						companyId: companyId,
						integrationId: integrationId
					}
				),
				{
					firstName: parameters.firstName,
					email: parameters.email,
					customerId: parameters.customerId
				}
			);

			commit(SET_REGISTER_CUSTOMER, postRegisterCustomer);
		},
		async unlinkAccountAction(
			{ commit },
			{ companyId, integrationId, rewardsCustomerId, errorHandler }
		) {
			let unlinkResult = await api.post(
				buildRoute(
					'/oem/company/{companyId}/rewards/{integrationId}/account/unlink',
					{
						companyId: companyId,
						integrationId: integrationId
					}
				),
				{
					rewardsCustomerId: rewardsCustomerId
				},
				errorHandler
			);

			if (unlinkResult) commit(SET_UNLINK_ACCOUNT_INFO, unlinkResult);
		},
		async linkAccountAction(
			{ commit },
			{ companyId, integrationId, member }
		) {
			let linkResult = await api.post(
				buildRoute(
					'/oem/company/{companyId}/rewards/{integrationId}/account/link',
					{
						companyId: companyId,
						integrationId: integrationId
					}
				),
				member
			);

			commit(SET_ACCOUNT_INFO, linkResult);
		},
		async submitRedemptionAction(
			{ commit },
			{ companyId, integrationId, model, customErrorHandler }
		) {
			const submitRedemption = await api.post(
				buildRoute(
					'/oem/company/{companyId}/rewards/{integrationId}/redemption/submit',
					{
						companyId: companyId,
						integrationId: integrationId
					}
				),
				model,
				customErrorHandler
			);

			commit(SET_SUBMIT_REDEMPTION, submitRedemption);
		},
		async voidRedemptionAction(
			{ commit },
			{ companyId, integrationId, entityId, entityTypeId, voidReason }
		) {
			const voidRedemption = await api.post(
				buildRoute(
					'/oem/company/{companyId}/rewards/{integrationId}/redemption/void',
					{
						companyId: companyId,
						integrationId: integrationId,
						model: {
							EntityId: entityId,
							EntityTypeId: entityTypeId,
							VoidReason: voidReason
						}
					}
				)
			);

			commit(SET_VOID_REDEMPTION, voidRedemption);
		}
	},
	getters: {
		accountInfo: state => state.accountInfo,
		unlinkAccountInfo: state => state.unlinkAccountInfo,
		dealershipEligible: state => {
			let result = true;

			if (
				state.accountInfo &&
				state.accountInfo.accountStatus ===
					state.accountStatuses.DealerNotEligible
			) {
				result = false;
			}

			return result;
		},
		rewardsCustomerId: state => {
			let result;

			if (state.accountInfo) {
				result = state.accountInfo.rewardsCustomerId;
			}

			return result;
		},
		memberNumber: state => {
			let result;

			if (state.accountInfo && state.accountInfo.member) {
				result = state.accountInfo.member.memberNumber;
			}

			return result;
		},
		accountInactiveUnlinked: state => {
			let result = false;

			if (
				state.accountInfo &&
				(state.accountInfo.accountStatus ===
					state.accountStatuses.InactiveUnlinked ||
					state.accountInfo.member.accountStatus ==
						state.accountStatuses.InactiveUnlinked)
			) {
				result = true;
			}

			return result;
		},
		accountInactive: state => {
			let result = false;

			if (
				state.accountInfo &&
				state.accountInfo.accountStatus ===
					state.accountStatuses.Inactive
			) {
				result = true;
			}

			return result;
		},
		memberLookupInactive: state => {
			let result = false;

			if (
				state.accountInfo &&
				state.accountInfo.member &&
				state.accountInfo.member.accountStatus ==
					state.accountStatuses.Inactive
			) {
				result = true;
			}

			return result;
		},
		accountDataMismatch: state => {
			let result = false;

			if (
				state.accountInfo &&
				state.accountInfo.accountStatus ===
					state.accountStatuses.DataMismatch
			) {
				result = true;
			}

			return result;
		},
		memberDataMismatches: state => {
			let result = [];

			if (state.accountInfo) {
				result = state.accountInfo.memberDataMismatches;
			}

			return result;
		},
		member: state => {
			let result = [];

			if (state.accountInfo) {
				result = state.accountInfo.member;
			}

			return result;
		},
		registrationIncomplete: state => {
			let result = false;
			if (
				state.accountInfo &&
				state.accountInfo.accountStatus ===
					state.accountStatuses.RegistrationIncomplete
			) {
				result = true;
			}

			return result;
		},
		accountFound: state => {
			let result = false;

			if (
				state.accountInfo &&
				state.accountInfo.accountStatus ===
					state.accountStatuses.Eligible
			) {
				result = true;
			}

			return result;
		},
		unlinked: state => {
			let result = false;

			if (
				state.accountInfo &&
				state.accountInfo.accountStatus ===
					state.accountStatuses.Unlinked
			) {
				result = true;
			}

			return result;
		},
		redeemableAmount: state => {
			let result = 0;

			if (
				state.accountInfo &&
				state.accountInfo.redeemableAmount !== undefined
			) {
				result = state.accountInfo.redeemableAmount;
			}

			return result;
		},
		redeemablePoints: state => {
			let result = 0;

			if (
				state.accountInfo &&
				state.accountInfo.redeemablePoints !== undefined
			) {
				result = state.accountInfo.redeemablePoints;
			}

			return result;
		},
		totalDollars: state => {
			let result = 0;

			if (
				state.accountInfo &&
				state.accountInfo.totalDollars !== undefined
			) {
				result = state.accountInfo.totalDollars;
			}

			return result;
		},
		totalPoints: state => {
			let result = 0;

			if (
				state.accountInfo &&
				state.accountInfo.totalPoints !== undefined
			) {
				result = state.accountInfo.totalPoints;
			}

			return result;
		},
		tier: state => {
			let result = '';

			if (state.accountInfo && state.accountInfo.tier !== undefined) {
				result = state.accountInfo.tier;
			}

			return result;
		},
		tierDisplayName: state => {
			let result = '';

			if (
				state.accountInfo &&
				state.accountInfo.tierDisplayName !== undefined
			) {
				result = state.accountInfo.tierDisplayName;
			}

			return result;
		},
		redemptionSummary: state => state.redemptionSummary,
		registerCustomer: state => state.registerCustomer,
		submitRedemption: state => state.submitRedemption,
		voidRedemption: state => state.voidRedemption
	}
};
